import {useEffect, useRef, useState} from 'react';
import {isOk} from '../../utils/apiResponse';
import {previewAccountRule} from './ga4AttributionApi';
import {ruleFromPayload} from './attributionModel';

/** Igual que la vista previa de los filtros GA4: se pide cuando el usuario deja de tocar. */
export const PREVIEW_DEBOUNCE_MS = 700;

/**
 * Qué cruzaría una regla en una cuenta, con los últimos 30 días de la propiedad GA4 del cliente
 * (patrón `useGa4FiltersPreview`).
 *
 * - Espera 700 ms después del último cambio: cada `<select>` es una consulta a la Data API de GA4.
 * - Una respuesta que llega después de otra pedida más tarde se descarta (`requestId`): con la red
 *   lenta, la vista previa mostraría la regla anterior bajo los controles de la nueva.
 * - Sin propiedad no pide nada: el backend la necesita y la pantalla ya dice que falta.
 * - `rule = null` pide la regla efectiva de la cuenta (Google, que no tiene parámetros que mandar).
 *
 * Las dependencias del efecto son valores, no objetos: el que llama arma la regla en cada render.
 */
export function useGa4AttributionPreview(clientAdAccountId, rule, propertyId) {
    const signature = rule ? JSON.stringify(ruleFromPayload(rule)) : '';
    const requestId = useRef(0);
    const lastPropertyId = useRef(propertyId);
    const [state, setState] = useState({loading: false, data: null, errorCode: null});
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!propertyId || !clientAdAccountId) {
            requestId.current += 1;
            setState({loading: false, data: null, errorCode: null});
            return undefined;
        }
        const id = requestId.current + 1;
        requestId.current = id;
        const propertyChanged = lastPropertyId.current !== propertyId;
        lastPropertyId.current = propertyId;
        // Con otra regla se conserva el resultado anterior, atenuado, mientras carga; con OTRA
        // propiedad no: serían los números de la anterior bajo el nombre de la nueva.
        setState((prev) => (propertyChanged
            ? {loading: true, data: null, errorCode: null}
            : {...prev, loading: true, errorCode: null}));
        const timer = setTimeout(() => {
            previewAccountRule(clientAdAccountId, {propertyId, rule: signature ? JSON.parse(signature) : null})
                .then((response) => {
                    if (requestId.current !== id) return;
                    if (response && response.absent) {
                        setState({loading: false, data: null, errorCode: null});
                    } else if (isOk(response) && response.data) {
                        setState({loading: false, data: response.data, errorCode: null});
                    } else {
                        setState({loading: false, data: null, errorCode: (response && response.status) || 'ERROR'});
                    }
                })
                .catch(() => {
                    if (requestId.current !== id) return;
                    setState({loading: false, data: null, errorCode: 'NETWORK_ERROR'});
                });
        }, PREVIEW_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [clientAdAccountId, propertyId, signature, attempt]);

    return {...state, retry: () => setAttempt((n) => n + 1)};
}

export default useGa4AttributionPreview;
